import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

/**
 * Undertow (Maelstrom Verge) — an arena-wide current: the whole arena pushes
 * the player toward one side wall while ranks of bullets sweep in FROM that
 * wall, against the flow. The push is well under the player's move speed, so
 * the fight is walking upstream while weaving through the oncoming ranks —
 * each rank has one clear gap, and the gap drifts rank to rank. Dashing
 * ignores the current (same "cut through the water" rule as Riptide Vortex).
 */
export class Undertow extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Undertow', telegraphDuration: 1.3, activeDuration: 3.0, minPhase, arenaControl: true });
    this.config = config;
  }

  onBegin(_boss, player) {
    // Push toward whichever side wall is farther from the player — maximum
    // runway to fight the current, and the bullet ranks get the whole arena
    // to sweep through.
    const mid = ARENA.x + ARENA.width / 2;
    this._sign = player.x < mid ? 1 : -1;
    this._rankTimer = 0;
    this._flow = 0;
    this._gapY = ARENA.y + ARENA.height * (0.25 + Math.random() * 0.5);
  }

  onActiveTick(dt, boss, player, pool) {
    this._flow += dt;
    if (!player.isDashing) {
      player.x += this._sign * this.config.pushStrength * dt;
      player.x = Math.min(ARENA.x + ARENA.width - player.radius, Math.max(ARENA.x + player.radius, player.x));
    }

    this._rankTimer -= dt;
    if (this._rankTimer <= 0) {
      this._rankTimer = this.config.rankInterval;
      // The safe gap drifts a bounded random walk rank to rank — readable,
      // but you can't park in one lane for the whole pattern.
      this._gapY += (Math.random() - 0.5) * 90;
      this._gapY = Math.min(ARENA.y + ARENA.height - 40, Math.max(ARENA.y + 40, this._gapY));
      const gapHalf = Math.max(22, this.config.gapSize / 2 - boss.phase * 3);
      const spawnX = this._sign > 0 ? ARENA.x + ARENA.width - 4 : ARENA.x + 4;
      for (let y = ARENA.y + 10; y <= ARENA.y + ARENA.height - 10; y += this.config.rankSpacing) {
        if (Math.abs(y - this._gapY) < gapHalf) continue;
        pool.fire({
          x: spawnX,
          y,
          vx: -this._sign * this.config.bulletSpeed,
          vy: 0,
          radius: 3,
          damage: this.config.bulletDamage,
          color: '#4dd8c7',
          lifetime: (ARENA.width + 20) / this.config.bulletSpeed,
        });
      }
    }
  }

  getVisual() {
    if (this.state !== PatternState.TELEGRAPH && this.state !== PatternState.ACTIVE) return null;
    return {
      kind: 'currentFlow',
      sign: this._sign,
      t: this._flow,
      active: this.state === PatternState.ACTIVE,
    };
  }
}
